import * as React from 'react';
import { createPortal } from 'react-dom';

import { Flex } from '@lightspeed/flame/Core';
import { IconWarning } from '@lightspeed/flame/Icon/Warning';
import { Text } from '@lightspeed/flame/Text';

import { createReactRenderer, getReactNodes } from '../helpers/react-renderer';
import Monitoring from 'root/apps/shared_modules/Monitoring';

import { getMountProps } from './utils';

const generateErrorMessage = (type: string) => `${type} containing 'eCom' cannot be created`;

export interface EComValidationProps {
  label: string;
  labelText: string;
  errorMessageType: string;
  inputProps: {
    id: string | null;
    name: string | null;
    isDisabled?: boolean;
    value: string | null;
  };
  inputStyles: { [key: string]: unknown };
  labelStyles: { [key: string]: unknown };
  getCrumbs: (name: string | null) => { [key: string]: unknown };
  containerSelector?: string | undefined;
}

const EComValidationInput: React.FC<EComValidationProps> = ({
  label,
  labelText,
  errorMessageType,
  inputProps: { name, isDisabled, id, value },
  inputStyles,
  labelStyles,
  getCrumbs,
  containerSelector,
}) => {
  const [inputValue, setInputValue] = React.useState('');
  const [hasError, setHasError] = React.useState(false);
  const [errorPortalContainer, setErrorPortalContainer] = React.useState<Element | null>(null);
  const isEditingEcomEntitiesAllowed = window.lsCloud.Modules.hasModule(
    'omni.allow_edit_ecom_entities'
  );

  React.useEffect(() => {
    if (value) {
      setInputValue(value);
    }
  }, []);

  const ref = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    const isErrorContainerAlreadyShowing = document.getElementById('ecom-error-container');

    if (
      containerSelector &&
      hasError &&
      !isErrorContainerAlreadyShowing &&
      !isEditingEcomEntitiesAllowed
    ) {
      const container = (ref.current as HTMLDivElement).closest(containerSelector);
      setErrorPortalContainer(container);
    }
  }, [containerSelector, hasError, isEditingEcomEntitiesAllowed]);

  React.useEffect(() => {
    window.merchantos.asyncSubscribe.notifySubscribers('react.mount.topbar', {
      props: {
        crumbs: getCrumbs(name),
      },
    });
  }, [getCrumbs, name]);

  const saveButton = document.getElementById('saveButton');
  const isPanDetectionActive = window.lsCloud.Modules.hasModule('data.pan_detection.warn');
  const checkDoesStringHaveEcom = (typedString: string) => {
    // check is case insensitive
    // ecom cannot be included in the string at all ('ecom 15%', 'ecom banana' etc should NOT pass)
    const regex = new RegExp(`[eE][cC][oO][mM]`);
    const doesStringHaveEcom = regex.test(typedString);

    if (doesStringHaveEcom) {
      setHasError(true);
      Monitoring.addEvent('ecom_string.added', 'eComValidationInput', {
        description: `Merchant added restricted string (ecom) to ${label} field`,
      });
      return true;
    }

    setHasError(false);
    return false;
  };

  const errorContainer = () => (
    <Flex
      alignItems="center"
      color="#DE2E37"
      data-test="error-container"
      id="ecom-error-container"
      mt={1}
      mb={1}
    >
      <IconWarning color="#DE2E37" />
      <Text ml={1}>{generateErrorMessage(errorMessageType)}</Text>
    </Flex>
  );

  return (
    <Flex justifyContent="flex-start" maxWidth="100%" flexDirection="column" ref={ref}>
      <Flex alignItems="center">
        <div className="label" style={labelStyles}>
          <label htmlFor={label}>{labelText}</label>
        </div>
        <input
          data-validation="checkForCreditCardInfo"
          data-container-selector=".view_field_box"
          type="text"
          autoComplete="off"
          name={name || 'name'}
          placeholder={labelText}
          className="notranslate view_view string data_control"
          id={id || 'view_name'}
          style={inputStyles}
          maxLength={255}
          value={inputValue}
          data-test="eComValidationInput"
          disabled={isEditingEcomEntitiesAllowed ? false : isDisabled}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            const {
              target: { value },
            } = event;

            if (isPanDetectionActive) {
              window.validateField(event.target, {
                containerSelector: document.getElementById('view_name'),
              });
            }

            if (
              (value !== '' && !checkDoesStringHaveEcom(value)) ||
              (value !== '' && isEditingEcomEntitiesAllowed)
            ) {
              event.target.classList.add('has_changed');
              event.target.classList.remove('savedx');
              saveButton?.removeAttribute('disabled');
              saveButton?.classList.add('dirty');
            } else {
              saveButton?.classList.remove('dirty');
              event.target.classList.remove('has_changed');
              event.target.classList.add('savedx');
              saveButton?.setAttribute('disabled', 'true');
            }
            setInputValue(value);
          }}
          onBlur={(event) => {
            if (isPanDetectionActive) {
              window.validateField(event.target, {
                containerSelector: document.getElementById('view_name'),
              });
            }
          }}
        />
      </Flex>
      {hasError && !containerSelector && !isEditingEcomEntitiesAllowed ? errorContainer() : null}
      {errorPortalContainer && !isEditingEcomEntitiesAllowed
        ? createPortal(errorContainer(), errorPortalContainer)
        : null}
    </Flex>
  );
};

const RenderedComponent = createReactRenderer({
  component: EComValidationInput,
  selector: '[data-react-root="EComValidationInput"]',
});

history.onChangeState(
  [
    { name: 'admin.views.class', id: undefined, form_name: undefined },
    { name: 'admin.views.sales_tax', id: undefined, form_name: 'view', tab: 'details' },
    { name: 'admin.views.employee', id: undefined, form_name: 'view', tab: 'details' },
  ],
  (filters: HistoryFilter) => {
    const matchingElements = getReactNodes('[data-react-root="EComValidationInput"]').length;
    const currentElement = getReactNodes('[data-react-root="EComValidationInput"]').item(0);
    const currentElementProps = currentElement?.getAttribute('data-react-props') as string;

    if (currentElementProps) {
      let allProps = [JSON.parse(currentElementProps)];
      if (matchingElements > 1) {
        getReactNodes('[data-react-root="EComValidationInput"]').forEach((node, index) => {
          if (index !== 0) {
            allProps = [...allProps, JSON.parse(node.getAttribute('data-react-props') as string)];
          }
        });
      }

      if (currentElement && currentElementProps) {
        RenderedComponent.mount({
          props: getMountProps(filters, allProps),
          ...JSON.parse(currentElementProps),
        });
      }

      RenderedComponent.mount();
    }
  },
  () => {
    RenderedComponent.unmount();
  }
);

export default EComValidationInput;
